import {
  ConfigurationRuleScopeEntityType,
  ConfigurationRuleSetupType,
  ProblemSeverity,
  ValidationProblemCode,
  ValidationProblemResponse,
} from '@ydistri/api-sdk';
import {
  iterableConfigurationRuleScopeEntityType,
  iterableConfigurationRuleSetupType,
} from '../../../helpers/types/iterableEnums';

/**
 * Reads the stores report the way the screen used to compute it: which rule a problem is about,
 * which entities it names, which fields a later exception overrides. The names below are the
 * server's (StoresAndDepartmentsRules); a rename there fails the scenarios, not the screen silently.
 */

export const RULE_ENTITY = 'ConfigurationRule';
export const STORE_ENTITY = 'Store';

const OUT_OF_SCOPE_ROLE = 'outOfScope';
const NO_STORES_ROLE = 'noStores';
const OVERRIDDEN_BY_ROLE = 'overriddenBy';

/** The server names related entities in the singular; the scope types are plural. */
const relatedEntityType: Record<ConfigurationRuleScopeEntityType, string> = {
  [ConfigurationRuleScopeEntityType.Regions]: 'Region',
  [ConfigurationRuleScopeEntityType.Stores]: STORE_ENTITY,
  [ConfigurationRuleScopeEntityType.StoreTypes]: 'StoreType',
  [ConfigurationRuleScopeEntityType.Departments]: 'Department',
};

export type IdsByEntityType = Record<ConfigurationRuleScopeEntityType, number[]>;

const emptyIds = (): IdsByEntityType => ({
  [ConfigurationRuleScopeEntityType.Regions]: [],
  [ConfigurationRuleScopeEntityType.Stores]: [],
  [ConfigurationRuleScopeEntityType.StoreTypes]: [],
  [ConfigurationRuleScopeEntityType.Departments]: [],
});

const isAboutRule = (p: ValidationProblemResponse, ruleId: number): boolean =>
  p.target?.entityType === RULE_ENTITY && p.target.entityId === String(ruleId);

export const problemsOfRule = (
  problems: ValidationProblemResponse[] | undefined,
  ruleId: number,
): ValidationProblemResponse[] => (problems ?? []).filter(p => isAboutRule(p, ruleId));

/**
 * The card of a rule: red for an Error, rose for a Warning. An Info (B20, a field a later exception
 * overrides) marks the field itself with the triangle and leaves the card alone, as it always did.
 */
export const ruleMark = (
  problems: ValidationProblemResponse[] | undefined,
  ruleId: number,
): 'error' | 'warning' | undefined => {
  const own = problemsOfRule(problems, ruleId);
  if (own.some(p => p.severity === ProblemSeverity.Error)) return 'error';
  if (own.some(p => p.severity === ProblemSeverity.Warning)) return 'warning';
  return undefined;
};

/** What the card's colour is about, in the server's words: every Error and Warning on the rule, once. */
export const ruleMessages = (
  problems: ValidationProblemResponse[] | undefined,
  ruleId: number,
): string[] =>
  Array.from(
    new Set(
      problemsOfRule(problems, ruleId)
        .filter(p => p.severity !== ProblemSeverity.Info)
        .map(p => p.message),
    ),
  );

const relatedIds = (problems: ValidationProblemResponse[], role: string): IdsByEntityType => {
  const ids = emptyIds();
  problems.forEach(p => {
    (p.related ?? []).forEach(r => {
      if (r.role !== role || r.entityId === null || r.entityId === undefined) return;
      const id = Number(r.entityId);
      iterableConfigurationRuleScopeEntityType.forEach(entityType => {
        if (relatedEntityType[entityType] === r.entityType && !ids[entityType].includes(id)) {
          ids[entityType].push(id);
        }
      });
    });
  });
  return ids;
};

/** B9: selected regions or store types of an exception with no store left after the general scope. */
export const noStoresIds = (
  problems: ValidationProblemResponse[] | undefined,
  ruleId: number,
): IdsByEntityType =>
  relatedIds(
    problemsOfRule(problems, ruleId).filter(
      p => p.code === ValidationProblemCode.StoresAndDepartmentsExceptionNoStores,
    ),
    NO_STORES_ROLE,
  );

/** B11/B17: selected ids the parent scope filtered out, with the message the server wrote. */
export const missingEntities = (
  problems: ValidationProblemResponse[] | undefined,
  ruleId: number,
): { ids: IdsByEntityType; messages: string[]; totalCount: number } => {
  const own = problemsOfRule(problems, ruleId).filter(
    p => p.code === ValidationProblemCode.StoresAndDepartmentsMissingEntities,
  );
  const ids = relatedIds(own, OUT_OF_SCOPE_ROLE);
  return {
    ids,
    messages: own.map(p => p.message),
    totalCount: iterableConfigurationRuleScopeEntityType.reduce((n, t) => n + ids[t].length, 0),
  };
};

/** B18: the stores of the general scope the server flagged, with its reason. */
export const flaggedStores = (
  problems: ValidationProblemResponse[] | undefined,
): { id: number; message: string; data: Record<string, string | undefined> }[] =>
  (problems ?? [])
    .filter(
      p =>
        p.code === ValidationProblemCode.StoresAndDepartmentsFlaggedStores &&
        p.target?.entityType === STORE_ENTITY &&
        p.target.entityId,
    )
    .map(p => ({ id: Number(p.target?.entityId), message: p.message, data: p.data ?? {} }));

const setupTypeOfPath = (path: string | null | undefined): ConfigurationRuleSetupType | undefined =>
  iterableConfigurationRuleSetupType.find(
    type => type.charAt(0).toLowerCase() + type.slice(1) === path,
  );

/** B20: for each field of a rule, the later exceptions that override it on an overlapping store. */
export const overriddenFields = (
  problems: ValidationProblemResponse[] | undefined,
  ruleId: number,
): Partial<Record<ConfigurationRuleSetupType, number[]>> => {
  const result: Partial<Record<ConfigurationRuleSetupType, number[]>> = {};
  problemsOfRule(problems, ruleId)
    .filter(p => p.code === ValidationProblemCode.StoresAndDepartmentsFieldOverridden)
    .forEach(p => {
      const field = setupTypeOfPath(p.path);
      if (!field) return;
      result[field] = (p.related ?? [])
        .filter(r => r.role === OVERRIDDEN_BY_ROLE && r.entityType === RULE_ENTITY)
        .map(r => Number(r.entityId));
    });
  return result;
};
